import { BigNumber } from '@0x/utils'
import errorUtils from 'bzx-common/src/lib/errorUtils'
import * as mobx from 'mobx'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import { Airdrop, airdrops, IAirdropInfo } from '../services/AirdropService'
import RootStore from './RootStore'

interface IAirdropStoreProps {
  airdropInfoPending: boolean
  airdropPending: boolean
  airdropsInfo: Record<Airdrop, IAirdropInfo>
  airdropsInfoLoaded: boolean
  balAirdropInfo: IAirdropInfo
  error: Error | null
  lockedBzrxAirdrops: IAirdropInfo[]
  rootStore: RootStore
}

/**
 * Default state for an airdrop
 */
function getInitialAirdropState(airdrop: Airdrop): IAirdropInfo {
  return {
    airdrop,
    amount: new BigNumber(0),
    airdropIndex: airdrops[airdrop].index,
    index: new BigNumber(0),
    proof: [],
    token: { symbol: '', address: '', decimals: 18 },
    userClaimed: false,
  }
}

function initAirdropsState(): Record<Airdrop, IAirdropInfo> {
  return {
    BAL: getInitialAirdropState(Airdrop.BAL),
    BZRX_COMPENSATION1: getInitialAirdropState(Airdrop.BZRX_COMPENSATION1),
    BZRX_COMPENSATION2: getInitialAirdropState(Airdrop.BZRX_COMPENSATION2),
    // Note BZRX_LOCKED state is there to avoid type issues for the Record; it is not used anymore.
    BZRX_LOCKED: getInitialAirdropState(Airdrop.BZRX_LOCKED),
    P125_COMPENSATION1: getInitialAirdropState(Airdrop.P125_COMPENSATION1),
  }
}

export default class AirdropStore implements IAirdropStoreProps {
  rootStore: RootStore
  fulcrumProvider: FulcrumProvider
  airdropPending = false
  error: Error | null = null
  airdropsInfoLoaded = false
  airdropsInfo = initAirdropsState()
  airdropInfoPending = false
  balAirdropInfo = getInitialAirdropState(Airdrop.BAL)
  lockedBzrxAirdrops: IAirdropInfo[] = []

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IAirdropStoreProps, value: any) {
    this[prop] = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<IAirdropStoreProps>) {
    Object.assign(this, props)
  }

  clearData() {
    this.airdropPending = false
    this.error = null
    this.balAirdropInfo = getInitialAirdropState(Airdrop.BAL)
    this.airdropsInfoLoaded = false
    this.airdropsInfo = initAirdropsState()
  }

  clearError() {
    this.error = null
  }

  /**
   * eg: `getUserAirdropInfo(Airdrop.BAL, 'address')`
   * sets the user claim info to `AirdropStore.airdropsInfo[Airdrop.BAL]`
   */
  async getUserAirdropInfo(airdrop: Airdrop, walletAddress: string) {
    const { services } = this.rootStore

    try {
      this.assign({ error: null, airdropInfoPending: true, airdropsInfoLoaded: false })
      const info = await services.airdropService.checkUserAirdrop(airdrop, walletAddress)
      mobx.runInAction(() => {
        this.airdropsInfo[airdrop] = info
      })
      return info
    } catch (err) {
      console.error(err)
      this.assign({ error: err })
    } finally {
      this.assign({ airdropInfoPending: false, airdropsInfoLoaded: true })
    }
  }

  async checkBalAirdrop() {
    const { web3Connection, services } = this.rootStore
    const { chainId, walletAddress } = web3Connection
    const { index, airdropChainId } = airdrops.BAL

    if (!chainId || services.airdropService.wasChecked(airdropChainId, index, walletAddress)) {
      return
    }
    try {
      this.assign({ error: null })
      const info = await services.airdropService.checkUserAirdrop(Airdrop.BAL, walletAddress)
      !info.userClaimed && this.assign({ balAirdropInfo: info })
    } catch (err) {
      console.error(err)
      this.assign({ error: err })
    }
  }

  async claimAirdrop(airdropInfo: IAirdropInfo) {
    const { transactionStore, services, web3Connection } = this.rootStore
    const { airdropService } = services
    const { chainId, walletAddress } = web3Connection
    const { airdropIndex } = airdropInfo
    let tx

    if (!chainId) {
      return
    }
    try {
      this.assign({ error: null, airdropPending: true })
      const txSteps = airdropService.claimAirdrop(airdropInfo, walletAddress)
      tx = await transactionStore.run('get-airdrop', txSteps)
      if (tx.status === 'success') {
        airdropService.setChecked(chainId, airdropIndex, walletAddress)
      }
      return tx
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Airdrop claim Failed' })
      this.set('error', err)
      console.error(err)
      return tx
    } finally {
      this.set('airdropPending', false)
    }
  }

  /** Check multiple airdrops, one after another. Stores the data in AirdropStore */
  async checkAirdrops(airdrops: Airdrop[], address: string) {
    for (const airdrop of airdrops) {
      await this.getUserAirdropInfo(airdrop, address)
    }
  }

  async checkBzrxLockedAirdrops(address: string) {
    this.assign({ error: null, airdropInfoPending: true, airdropsInfoLoaded: false })
    try {
      const { services } = this.rootStore
      const { airdropService } = services
      const lockedBzrxAirdrops = await airdropService.checkBzrxLockedAirdrops(address)
      this.assign({ lockedBzrxAirdrops })
      return lockedBzrxAirdrops
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Failed to list your airdrops' })
      console.error(err)
      this.assign({ error: err })
    } finally {
      this.assign({ airdropInfoPending: false, airdropsInfoLoaded: true })
    }
  }

  claimBZRXCompensation(airdrop: Airdrop) {
    return this.claimAirdrop(this.airdropsInfo[airdrop])
  }

  claimLockedBZRXCompensation(airdropIndex: number) {
    const airdropInfo = this.lockedBzrxAirdrops.find(
      (airdrop) => airdrop.airdropIndex === airdropIndex
    )
    if (!airdropInfo) {
      throw new Error('locked bzrx airdropInfo was not found')
    }
    return this.claimAirdrop(airdropInfo)
  }

  claimP125Compensation1() {
    return this.claimAirdrop(this.airdropsInfo.P125_COMPENSATION1)
  }

  claimBalAirdrop() {
    return this.claimAirdrop(this.balAirdropInfo)
  }

  constructor(rootStore: RootStore) {
    const { fulcrumProvider } = rootStore
    this.rootStore = rootStore
    this.fulcrumProvider = fulcrumProvider
    mobx.makeAutoObservable(
      this,
      { airdropsInfo: mobx.observable.shallow, rootStore: false },
      { deep: false, autoBind: true }
    )
  }
}
